import { Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { IoArrowBackSharp } from "react-icons/io5";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import { Parameter } from "../../models/Parameter";
import { KpiService } from "../../services/KpiService";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import TableComponent from "../../components/TableComponent";
import SelectComponent from "../../components/SelectComponent";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { Bar } from "react-chartjs-2";
import { ChartOptions } from 'chart.js';
import Routes from "../../helpers/Routes";
import { useNavigateToClientAssistancesHistoryDashboard } from "../../ClientUtilities";

export default function KpiClientDashboard() {
    const intl = useIntl();
    const navigate = useNavigate();
    const kpiService = new KpiService();
    const pageSizes: number[] = [5, 15, 30];
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes));
    const [erpId, setErpId] = useState<string>(Utilities.LoadParameterFromURLQuery("erpId", "string", ""));
    const [name, setName] = useState<string>(Utilities.LoadParameterFromURLQuery("name", "string", ""));
    const [numAssists, setNumAssists] = useState<string>(Utilities.LoadParameterFromURLQuery("numAssistd", "number", ""));
    const [currentPage, setCurrentPage] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    interface ChartData {
        labels: string[];
        datasets: {
            label: string;
            data: number[];
            clientErpIds: string[];
            clientNames: string[];
            backgroundColor: string;
        }[];
    }

    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="Screens.Kpi.ClientsDashboard.Table.ErpId" />,
                accessor: "erpId",
                width: 15,
            },
            {
                Header: <FormattedMessage id="Screens.Kpi.ClientsDashboard.Table.Name" />,
                accessor: "name",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.Kpi.ClientsDashboard.Table.NumAssistances" />,
                accessor: "numAssists",
                width: 15,
            },
            {
                Header: <FormattedMessage id="Screens.Kpi.ClientsDashboard.Table.NumAssistancesAccumulated" />,
                accessor: "numAssistsAccumulated",
                width: 20,
                disableSortBy: true,
            },
            {
                Header: <FormattedMessage id="Screens.Kpi.ClientsDashboard.Table.PercentAssistances" />,
                accessor: "assistsPercentage",
                width: 15,
                disableSortBy: true,
                Cell: (props: any) => {
                    return <span>{`${props.cell.value}%`}</span>;
                },
            },
            {
                Header: <FormattedMessage id="Screens.Kpi.ClientsDashboard.Table.PercentAssistancesAccumulated" />,
                accessor: "assistsAccumulatedPercentage",
                width: 20,
                disableSortBy: true,
                Cell: (props: any) => {
                    return <span>{`${props.cell.value}%`}</span>;
                },
            },
        ],
        []
    );

    const goBack = () => {
        navigate("/kpis");
    };

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null
    ) => {
        //Atualizamos na history do browser a current page e pageSize

        var searchUrl = `?page=${page}&pageSize=${pageSize}` +
            `${Utilities.AddParamToQueryIfValue("erpId", "string", erpId)}` +
            `${Utilities.AddParamToQueryIfValue("name", "string", name)}` +
            `${Utilities.AddParamToQueryIfValue("numAssists", "number", numAssists)}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }

        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        if (searchParams.toString() === "") {
            navigate(`/kpis/Clients/Dashboard${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/kpis/Clients/Dashboard${searchUrl}`);
        }

        setLoading(true);

        var response = await kpiService.GetClientsDashboardData(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(5);
            setCurrentPage(1);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        if (response.obj.clientsData && response.obj.clientsData.items.length === 0) {
            setCurrentPage(1);
            setGoToPage(1);
            Toast.Show("info", "Não existe dados de nenhum cliente");
        }

        if (response.obj.clientsData) {
            setData(response.obj.clientsData.items);
            setPageCount(response.obj.clientsData.totalPages);
            setLoading(false);
            setCurrentPageSize(pageSize);
            setCurrentPage(page);
            setCurrentSorting(sortBy);
            setGoToPage(0);

            if (response.obj.clientsData.totalPages > 0 && page > response.obj.clientsData.totalPages) {
                setGoToPage(1);
            }
        }
        if (response.obj.topClients && response.obj.topClients.length > 0) {
            const labels = response.obj.topClients.map((client: any) => client.name);
            const data = response.obj.topClients.map((client: any) => client.numAssists);
            const clientErpIds = response.obj.topClients.map((client: any) => client.erpId);
            const clientNames = response.obj.topClients.map((client: any) => client.name);

            setChartData({
                labels: labels,
                datasets: [
                    {
                        label: 'Assistências por Cliente',
                        data: data,
                        clientErpIds: clientErpIds,
                        clientNames: clientNames,
                        backgroundColor: 'rgba(21,96,130,255)',
                    },
                ],
            });
        };
    }

    const mountSearchParameters = (): Parameter[] => {
        const parameters: Parameter[] = [];
        parameters.push(new Parameter("erpId", erpId));
        parameters.push(new Parameter("name", name));
        parameters.push(new Parameter("numAssists", numAssists));
        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onRemoveFiltersClick = () => {
        setCurrentPage(1);
        setGoToPage(1);
        setErpId("");
        setName("");
        setNumAssists("");
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
    }, [reloadSearch]);

    const [chartData, setChartData] = useState<ChartData>({
        labels: [],
        datasets: [
            {
                label: 'Assistências por Cliente',
                data: [],
                clientErpIds: [],
                clientNames: [],
                backgroundColor: 'rgba(21,96,130,255)',
            },
        ],
    });

    const chartOptions: ChartOptions<'bar'> = {
        responsive: true,
        plugins: {
            tooltip: {
                enabled: true,
            },
            legend: {
                position: "bottom" as const,
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: {
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    callback: function (value, index) {
                        const label = chartData.labels[index];
                        const maxLengthPerLine = 20;


                        const lines = [];
                        for (let i = 0; i < label.length; i += maxLengthPerLine) {
                            lines.push(label.substring(i, i + maxLengthPerLine));
                        }

                        return lines;
                    },
                    padding: 10,
                },
            },
            y: {
                beginAtZero: true,
            },
        },
        layout: {
            padding: {
                left: 10,
                right: 10,
                top: 10,
                bottom: 10,
            },
        },
        elements: {
            bar: {
                borderWidth: 2,
            },
        },
        onClick: (event: any, chartColumns: any[]) => {
            if (chartColumns.length > 0) {
                const elementIndex = chartColumns[0].index;
                const clientId = chartData.datasets[0].clientErpIds[elementIndex];
                const clientName = chartData.datasets[0].clientNames[elementIndex];

                if (clientId) {
                    navigateToClientAssistancesHistoryDashboard(clientId, clientName)
                }
            }
        },
    };

    //#region Navegar ClientAssistancesHistoryDashboard
    const navigateToClientAssistancesHistoryDashboard = useNavigateToClientAssistancesHistoryDashboard();
    //endregion


    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <Row style={{ margin: "0px", padding: "0px" }}>
                            <Col style={{ margin: "0px", padding: "0px" }}>
                                <span className="pageTitle">
                                    <IoArrowBackSharp className="pageTitleBackIcon" onClick={goBack} />
                                    <FormattedMessage id="Screens.Kpi.ClientsDashboard.Title" />
                                </span>
                            </Col>
                        </Row>
                    </div>
                    <div>
                        <h2>Assistências por Cliente</h2>
                        <Bar data={chartData} options={chartOptions} height={50} />
                    </div>

                    <div className="pageBody">
                        <Row>
                            <InputComponent
                                xl={1}
                                lg={2}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ width: "100%", height: "43px" }}
                                type={"number"}
                                label={intl.formatMessage({
                                    id: "Screens.Kpi.ClientsDashboard.Filters.ErpId",
                                })}
                                readOnly={false}
                                value={erpId}
                                onEnter={onEnter}
                                onChange={(t: string) => setErpId(t)}
                            />
                            <InputComponent
                                xl={2}
                                lg={2}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ width: "100%", height: "43px" }}
                                type={"text"}
                                label={intl.formatMessage({
                                    id: "Screens.Kpi.ClientsDashboard.Filters.Name",
                                })}
                                readOnly={false}
                                value={name}
                                onEnter={onEnter}
                                onChange={(t: string) => setName(t)}
                            />
                        </Row>

                        <Row>
                            <Col
                                xl={6}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                />
                            </Col>
                        </Row>
                        <br></br>
                        <TableComponent
                            goToPage={goToPage}
                            loading={loading}
                            pageCount={pageCount}
                            columns={columns}
                            manualPagination={true}
                            fetchData={fetchData}
                            pageSizes={pageSizes}
                            initialPage={page}
                            initialPageSize={pageSize}
                            initialSortBy={initialSortBy}
                            data={data}
                            dontOrderFirstCollumn={true}
                        />
                    </div>
                </div>
            </Layout>
        </>
    );
}